import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, CheckCircle2, Circle, Loader2, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoad, act } from "@/lib/hooks";
import { api, fdatetime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PageHeader, Section, StatusBadge } from "@/components/common";
import { ImportWizard } from "@/components/import/ImportWizard";

const BADGE = { not_started: ["canceled", "Not started"], in_progress: ["pending", "In progress"], complete: ["success", "Complete"], skipped: ["locked", "Skipped"] };
const ICON = { not_started: Circle, in_progress: Loader2, complete: CheckCircle2, skipped: SkipForward };

function Instructions() {
  return (
    <Section title="Before you start" testId="qb-instructions">
      <div className="space-y-2 p-5 text-sm text-slate-600">
        <p>Pick a cut-over date (usually a month end). In QuickBooks, export each list or report below as CSV, as of that date. Then import the files here in order. You can stop and come back at any time; progress is saved.</p>
        <p>Open invoices and open bills post against <strong>3900 Opening Balance Equity</strong>, not revenue or expenses, because QuickBooks already recorded those. Receivable and payable lines in the trial balance are ignored, so nothing is counted twice.</p>
      </div>
    </Section>
  );
}

function StepRow({ s, i, open, onOpen, onSkip }) {
  const Icon = s.available ? ICON[s.status] : Lock;
  return (
    <li data-testid={`qb-step-${s.key}`} className={cn("flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between", open && "bg-slate-50")}>
      <div className="flex items-start gap-3">
        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", s.status === "complete" ? "text-emerald-600" : s.available ? "text-slate-400" : "text-slate-300")} />
        <div>
          <div className="font-medium text-slate-900">{i + 1}. {s.label}</div>
          <div className="text-xs text-slate-500">Export: {s.export}</div>
          {s.status === "complete" && <div className="mt-1 text-xs text-emerald-700" data-testid={`qb-step-${s.key}-done`}>{s.rows} row(s) imported · {fdatetime(s.at)}</div>}
          {s.status === "skipped" && <div className="mt-1 text-xs text-slate-500">Skipped · {fdatetime(s.at)}</div>}
          {!s.available && <div className="mt-1 text-xs text-slate-400">Available after the previous step is complete or skipped.</div>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={BADGE[s.status][0]} label={BADGE[s.status][1]} testId={`qb-step-${s.key}-status`} />
        <Button size="sm" variant="outline" disabled={!s.available} data-testid={`qb-step-${s.key}-open`} onClick={onOpen}>{open ? "Close" : s.status === "complete" ? "Import more" : "Import"}</Button>
        {s.available && s.status !== "complete" && s.status !== "skipped" && <Button size="sm" variant="ghost" data-testid={`qb-step-${s.key}-skip`} onClick={onSkip}>Skip</Button>}
      </div>
    </li>
  );
}

export default function QuickBooksMigration() {
  const [d, reload] = useLoad("/imports/qb");
  const [open, setOpen] = useState(null);
  if (!d) return null;
  const done = d.steps.filter((s) => ["complete", "skipped"].includes(s.status)).length;
  const skip = async (key) => { if (await act(() => api.post(`/imports/qb/${key}/skip`), "Step skipped")) { setOpen(null); reload(); } };
  const current = d.steps.find((s) => s.key === open);
  return (
    <div className="space-y-6">
      <PageHeader title="QuickBooks migration" subtitle={`${done} of ${d.steps.length} steps done`}>
        <Button asChild variant="outline" data-testid="back-to-import"><Link to="/app/settings/import"><ArrowLeft className="mr-1 h-4 w-4" />Data import</Link></Button>
      </PageHeader>
      <Instructions />
      <Section title="Migration checklist" testId="qb-checklist">
        <ol className="divide-y divide-slate-200">
          {d.steps.map((s, i) => <StepRow key={s.key} s={s} i={i} open={open === s.key} onOpen={() => setOpen(open === s.key ? null : s.key)} onSkip={() => skip(s.key)} />)}
        </ol>
      </Section>
      {current && (
        <Section title={`Import: ${current.label}`} testId="qb-step-wizard">
          <ImportWizard key={current.key} type={current.type} qbStep={current.key} onDone={reload} />
        </Section>
      )}
    </div>
  );
}
